import { useQuery } from '@tanstack/react-query';
import { baseUrl } from '../../../../../../baseUrl';
import { useState, useRef, useEffect, useCallback } from 'react';
import type { GridApi, GridReadyEvent } from 'ag-grid-enterprise';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { fetchWithAuthHandling } from '../../../../../../authHandling';
import { Container, Box, Typography, CircularProgress } from '@mui/material';
import { FormSubmissionTable } from '../../../../../../components/common/formSubmissionTable';
import { callsTableColumnDefs } from '../../../../../../components/buyers/calls/callsTableColumns';
import { isValidPhoneNumber, formatPhoneNumberE164 } from '../../../../../../utils/phoneValidator';

export const Route = createFileRoute(
    '/_app/buyers/$buyerId/_buyerLayout/calls/',
)({
    loader: ({ params }) => {
        const { buyerId } = params;
        return { buyerId };
    },
    component: CallsTab,
})

interface SortModel {
    colId: string;
    sort: "asc" | "desc";
}
interface AgentData {
    callRecords: Array<Record<string, unknown>>;
}

function CallsTab() {
    const { buyerId } = Route.useLoaderData();

    const [filters, setFilters] = useState({});
    const [sortModel] = useState<SortModel[]>([]);
    const [page, _] = useState(1);
    const [pageSize, setPageSize] = useState(2000);
    const [prevData, setPrevData] = useState<AgentData | null>(null);
    const gridApiRef = useRef<GridApi | null>(null);
    const gridColumnApiRef = useRef<GridApi>(null);
    const navigete = useNavigate();

    const { data, isLoading, error, isSuccess } = useQuery({
        queryKey: ["calls", { filters, sortModel, page, pageSize }],
        queryFn: async () => {
            const hasFilters = Object.keys(filters).length > 0;
            const hasSorting = sortModel.length > 0;

            if (hasFilters || hasSorting) {
                const body: {
                    page: number;
                    limit: number;
                    query: Record<string, unknown>;
                } = {
                    page,
                    limit: pageSize,
                    query: {}
                };

                if (hasFilters) {
                    const filterFields: Record<string, string> = {};
                    for (const [field, filter] of Object.entries(
                        filters as Record<string, { filter: string }>
                    )) {
                        const filterValue = filter.filter;

                        if (field === "emailAddress") {
                            setPageSize(1);
                        }
                        if (filterValue) {
                            if (field === "phoneNumber") {
                                try {
                                    const formattedPhoneNumber = isValidPhoneNumber(
                                        filterValue,
                                        "CA"
                                    )
                                        ? formatPhoneNumberE164(filterValue, "CA")
                                        : formatPhoneNumberE164(filterValue, "US")
                                            ? formatPhoneNumberE164(filterValue, "US")
                                            : filterValue;
                                    filterFields[field] = formattedPhoneNumber;
                                } catch {
                                    filterFields[field] = "";
                                }
                            } else {
                                filterFields[field] = filterValue;
                            }
                        }
                    }
                    body.query.filter = filterFields;
                }

                if (hasSorting) {
                    const firstSort = sortModel[0] as {
                        colId: string;
                        sort: "asc" | "desc";
                    };
                    const sortField = firstSort.colId;
                    const sortOrder = firstSort.sort === "asc" ? "ASC" : "DESC";

                    body.query.sort = {
                        order: sortOrder,
                        field: sortField
                    };
                }

                const response = await fetchWithAuthHandling(`${baseUrl}/${buyerId}/calls/search`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify(body)
                });

                setPageSize(2000);
                return response.json();
            }

            const params = new URLSearchParams({
                page: page.toString(),
                limit: pageSize.toString()
            });

            const response = await fetchWithAuthHandling(`${baseUrl}/buyers/${buyerId}/calls?${params.toString()}`, {
                method: "GET",
                credentials: "include",
            });

            return response.json();
        },
        retry: false
    });

    useEffect(() => {
        if (isSuccess) {
            setPrevData(data);
        }
    }, [isSuccess, data]);

    const onFilterChanged = useCallback(() => {
        const filterModel = gridApiRef.current?.getFilterModel();
        setFilters(filterModel || {});
    }, []);

    // const onSortChanged = useCallback(() => {
    //     if (gridColumnApiRef.current) {
    //         const sortModel = gridColumnApiRef.current
    //             .getColumnState()
    //             .filter((col: ColumnState) => col.sort !== null)
    //             .map((col: ColumnState) => ({
    //                 colId: col.colId,
    //                 sort: col.sort as "asc" | "desc"
    //             }));
    //         setSortModel(sortModel);
    //     }
    // }, []);

    const onGridReady = useCallback((params: GridReadyEvent) => {
        gridApiRef.current = params.api;
        gridColumnApiRef.current = params.api;
    }, []);

    const rowData = error ? prevData?.callRecords : data?.callRecords;

    if (!data && !isLoading) {
        return (
            <Container
                sx={{
                    my: 5,
                    maxWidth: {
                        xs: "97%"
                    }
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center"
                    }}
                >
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                        No Data Found
                    </Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Box>
            <Container
                sx={{
                    mt: 3,
                    maxWidth: {
                        xs: "100%"
                    }
                }}
                disableGutters={true}
            >
                {error && <Typography>Error loading data: {error.message}</Typography>}

                {isLoading && !prevData ? (
                    <Box
                        sx={{
                            display: "flex",
                            width: "100%",
                            alignItems: "center",
                            justifyContent: "center"
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center"
                        }}
                    >
                        <FormSubmissionTable
                            columnDefs={callsTableColumnDefs}
                            rowData={rowData || []}
                            onFilterChanged={onFilterChanged}
                            // onSortChanged={onSortChanged}
                            pageSize={pageSize}
                            onGridReady={onGridReady}
                            height="calc(100vh - 154px)"
                            onCellClicked={(e) => {
                                navigete({ to: `/buyers/${buyerId}/calls/${e.data.id}` });
                            }}
                        />
                    </Box>
                )}
            </Container>

        </Box>
    );
}